import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  annual: {
    path: '/annual-progress-term1',
    pageLabel: '연간진도표',
    openSelectors: [
      'a[href^="/annual-progress-term1"]',
      'a[href^="/annual-progress-term2"]',
      '.cp-sidebar-annual-progress-link',
    ],
    openLabel: '연간진도표',
    accessLabel: '사이드바 「교육과정 노트」',
  },
  slide: {
    pageLabel: '수업 프레젠테이션',
    accessLabel: '연간진도·프로그램 표의 「프레젠테이션 열기」(새 탭)',
    openLabel: '프레젠테이션 열기',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'go-annual-progress': R.annual,
  'select-cell': R.annual,
  'open-presentation': R.annual,
  'slide-overview': R.slide,
  'slide-local-only-hint': R.slide,
  'slide-pick-page': R.slide,
  'slide-tool-text': R.slide,
  'slide-tool-shape': R.slide,
  'slide-tool-link-image': R.slide,
  'slide-tool-clipboard-image': R.slide,
  'slide-tool-scan-image': R.slide,
  'slide-tool-video': R.slide,
  'slide-tool-webpage': R.slide,
  'slide-tool-code': R.slide,
  'slide-tool-link-button': R.slide,
  'slide-tool-dismiss': R.slide,
  'slide-tool-quiz-short': R.slide,
  'slide-tool-quiz-choice': R.slide,
  'slide-tool-undo': R.slide,
  'slide-tool-redo': R.slide,
  'slide-tool-snap': R.slide,
  'slide-tool-front': R.slide,
  'slide-tool-back': R.slide,
  'slide-tool-align': R.slide,
  'slide-force-add-page': R.slide,
  'slide-practice': R.slide,
  'nav-webcam': R.slide,
  'nav-webcam-pause': R.slide,
  'nav-timer': R.slide,
  'nav-timer-running': R.slide,
  'nav-bgm': R.slide,
  'nav-bgm-stop': R.slide,
  'nav-dice': R.slide,
  'nav-dice-modal': R.slide,
  'nav-caption': R.slide,
  'nav-caption-stop': R.slide,
  'nav-offline-html': R.slide,
  'open-question': R.slide,
  'question-overview': R.slide,
  'question-group': R.slide,
  'question-view-mode': R.slide,
  'question-sort': R.slide,
  'question-options': R.slide,
  'question-font': R.slide,
  'question-monitor': R.slide,
  'question-prompt': R.slide,
  'question-type-choice': R.slide,
  'question-choice-correct': R.slide,
  'question-type-short': R.slide,
  'question-short-multi': R.slide,
  'question-type-essay': R.slide,
  'question-essay-guide': R.slide,
  'question-detail-close': R.slide,
  'question-nav': R.slide,
};

const classPresentationRaw: TutorialDefinition = {
  id: 'class-presentation',
  title: '수업 프레젠테이션',
  summary:
    '연간진도표에서 수업 슬라이드를 새 탭으로 연 뒤, 개체·네비 도구·질문 페이지 흐름을 익혀 봐요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '수업 프레젠테이션 튜토리얼을 시작해요! 연간진도표에서 슬라이드를 새 탭으로 연 뒤, 도구·네비·질문 화면을 순서대로 살펴볼게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '수업 프레젠테이션을 시작해요!',
      placement: 'center',
    },
    {
      id: 'go-annual-progress',
      mascot:
        '사이드바에서 「1학기 연간진도표」또는 「2학기 연간진도표」를 눌러 이동해 주세요. 프로그램 표가 있는 화면이면 됩니다.',
      targetSelector:
        'a[href^="/annual-progress-term1"], a[href^="/annual-progress-term2"], .cp-sidebar-annual-progress-link',
      placement: 'right',
      advanceOnPathPrefix: ['/annual-progress-term1', '/annual-progress-term2', '/program/'],
      section: '[프레젠] 페이지 열기',
      sectionMascot: '연간진도·프로그램 표로 이동하는 구간이에요.',
    },
    {
      id: 'select-cell',
      mascot:
        '표에서 학습내용이 있는 칸(또는 행)을 하나 선택한 뒤 "다음"을 눌러 주세요. 선택한 행의 프레젠테이션을 열어요.',
      targetSelector:
        '.cp-annual-progress-table, .cp-program-progress-table, .cp-annual-progress-grid',
      manualAdvance: true,
      section: '[프레젠] 행 선택 · 열기',
      sectionMascot: '슬라이드를 열 수업을 고르는 구간이에요.',
    },
    {
      id: 'open-presentation',
      mascot:
        '상단 「프레젠테이션 열기」를 눌러 주세요. 새 탭에서 이어서 안내해요. 팝업이 막혀 있으면 브라우저에서 허용해 주세요.',
      targetSelector: '[data-tutorial-slide="open-presentation"]',
    },
    {
      id: 'slide-overview',
      mascot:
        '수업 슬라이드 편집 화면이에요. 편집모드에서 왼쪽·오른쪽 도구로 개체를 넣고, 아래쪽에서는 슬라이드를 추가할 수 있어요. 전체 구성을 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="page"]',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[프레젠] 화면 둘러보기',
      sectionMascot: '슬라이드 편집 화면을 알아보는 구간이에요.',
    },
    {
      id: 'slide-local-only-hint',
      mascot:
        '학습내용이 없는 셀에서 프레젠을 열면 이렇게 안내가 떠요. 편집은 되지만 서버에 저장되지 않고 이 브라우저에서만 쓸 수 있어요. 닫기(X)를 누르거나 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-slide="local-only-hint"], .cp-program-slide-owner-hint--floating',
      placement: 'bottom',
      advanceSelector:
        '[data-tutorial-slide="local-only-hint-close"], .cp-program-slide-owner-hint-close',
      advanceImmediately: true,
      advanceWhenSelectorGone:
        '[data-tutorial-slide="local-only-hint"], .cp-program-slide-owner-hint--floating',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      clickOnManualAdvanceSelectors:
        '[data-tutorial-slide="local-only-hint-close"], .cp-program-slide-owner-hint-close',
      targetMissingHint:
        '안내가 없거나 이미 닫혔다면 학습내용이 연결된 프레젠이거나 안내를 닫은 상태예요. "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'slide-pick-page',
      mascot:
        '하단의 숫자 슬라이드 탭(타이틀·질문이 아닌 쪽)을 눌러 편집할 슬라이드로 이동해 주세요.',
      targetSelector: '[data-tutorial-slide="tab-page"]',
      advanceSelector: '[data-tutorial-slide="tab-page"]',
      advanceImmediately: true,
      /** 하단 탭을 말풍선이 가리지 않도록 위쪽에 배치 */
      placement: 'top',
      section: '[프레젠] 도구 둘러보기',
      sectionMascot: '개체 블럭과 편집 도구를 알아보는 구간이에요.',
    },
    {
      id: 'slide-tool-text',
      mascot:
        '왼쪽 「텍스트 블럭」은 글상자를 슬라이드에 추가해요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="텍스트 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-shape',
      mascot:
        '「도형 블럭」메뉴에서는 직사각형·원형·직선 도형을 넣을 수 있어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="도형 블럭 추가 메뉴"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-link-image',
      mascot: '「링크 이미지」는 URL로 이미지를 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="링크 이미지 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-clipboard-image',
      mascot:
        '「클립보드 이미지」는 복사한 그림을 붙여넣어 넣어요(Ctrl+V). "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-slide="toolbar-left"] [aria-label="클립보드 이미지 블럭 추가 (Ctrl+V로 붙여넣기)"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-scan-image',
      mascot: '「스캔 이미지」는 웹캠·스캔으로 가져온 이미지를 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="스캔 이미지 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-video',
      mascot: '「동영상 블럭」은 수업용 영상을 슬라이드에 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="동영상 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-webpage',
      mascot: '「웹페이지 블럭」은 외부 웹 화면을 슬라이드에 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="웹페이지 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-code',
      mascot: '「소스코드 블럭」은 코드·HTML 미리보기를 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="소스코드 블럭 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-link-button',
      mascot: '「링크 버튼」은 클릭하면 링크로 이동하는 버튼을 만들어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="링크 버튼 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-dismiss',
      mascot:
        '「가림막 버튼」은 내용을 가렸다가 눌러서 드러내는 버튼이에요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="가림막 버튼 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-quiz-short',
      mascot: '「단답형 퀴즈」는 짧은 답을 받는 퀴즈를 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="단답형 퀴즈 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-quiz-choice',
      mascot: '「선택형 퀴즈」는 보기 중에서 고르는 퀴즈를 넣어요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-left"] [aria-label="선택형 퀴즈 추가"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-undo',
      mascot: '오른쪽 「되돌리기」는 바로 전 편집을 취소해요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-right"] [aria-label="편집 되돌리기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-redo',
      mascot: '「다시 실행」은 되돌리기를 다시 적용해요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-right"] [aria-label="편집 다시 실행"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-snap',
      mascot:
        '「스마트 스냅」은 개체를 움직일 때 정렬선을 도와주는 기능이에요. "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-slide="toolbar-right"] [aria-label="스마트 스냅 끄기"], [data-tutorial-slide="toolbar-right"] [aria-label="스마트 스냅 켜기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-front',
      mascot: '「맨앞으로 보내기」는 선택한 개체를 가장 위로 올려요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-right"] [aria-label="개체 맨앞으로 보내기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-back',
      mascot: '「맨뒤로 보내기」는 선택한 개체를 가장 아래로 내려요. "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="toolbar-right"] [aria-label="개체 맨뒤로 보내기"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'slide-tool-align',
      mascot:
        '정렬 도구로 왼쪽·중앙·오른쪽·위·아래 정렬과 가로·세로 등간격 정렬을 할 수 있어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-slide="toolbar-right"] [aria-label="왼쪽정렬"], [data-tutorial-slide="toolbar-right"] [aria-label="중앙정렬"], [data-tutorial-slide="toolbar-right"] [aria-label="오른쪽정렬"], [data-tutorial-slide="toolbar-right"] [aria-label="위쪽정렬"], [data-tutorial-slide="toolbar-right"] [aria-label="상하중앙정렬"], [data-tutorial-slide="toolbar-right"] [aria-label="아래정렬"], [data-tutorial-slide="toolbar-right"] [aria-label="가로 등간격 정렬"], [data-tutorial-slide="toolbar-right"] [aria-label="세로 등간격 정렬"]',
      manualAdvance: true,
      blockTargetClick: true,
      spotlightSeparate: true,
    },
    {
      id: 'slide-force-add-page',
      mascot: '하단 「슬라이드 추가」(+)를 눌러 새 슬라이드를 만들어 보세요.',
      targetSelector: '[data-tutorial-slide="add-slide"], [aria-label="슬라이드 추가"]',
      advanceImmediately: true,
      section: '[프레젠] 실습',
      sectionMascot: '슬라이드를 추가하고 자유롭게 써 보는 구간이에요.',
    },
    {
      id: 'slide-practice',
      mascot:
        '슬라이드를 자유롭게 구성해 보세요. 닫기·저장·HTML 다운로드와 네비의 웹캠·타이머·BGM·뽑기·자막은 잠겨 있어요. 앞서 본 도구로 충분히 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="page"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      blockClickSelectors:
        '[data-tutorial-slide="close"], [data-tutorial-slide="save"], [data-tutorial-slide="offline-html"], [data-tutorial-slide="webcam"], [data-tutorial-slide="webcam-pause"], [data-tutorial-slide="timer"], [data-tutorial-slide="bgm"], [data-tutorial-slide="dice"], [data-tutorial-slide="caption"], .cp-program-slide-topnav-center',
    },
    {
      id: 'nav-webcam',
      mascot:
        '상단 네비의 웹캠(실물화상기) 버튼을 눌러 미리보기를 켜 보세요. 권한 허용이 필요할 수 있어요. 켠 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="webcam"]',
      advanceWhenSelectorExists:
        '.cp-program-slide-webcam-canvas-sandwich, .cp-program-slide-webcam-nav-block.is-webcam-on',
      manualAdvanceIfExists:
        '.cp-program-slide-webcam-canvas-sandwich, .cp-program-slide-webcam-nav-block.is-webcam-on',
      allowManualAdvanceActions: true,
      section: '[프레젠] 네비 도구',
      sectionMascot: '웹캠·타이머·BGM·뽑기·자막을 써 보는 구간이에요.',
    },
    {
      id: 'nav-webcam-pause',
      mascot:
        '화면을 클릭하거나 상단의 일시정지 버튼을 누르면 화면이 멈춰요. 정지한 뒤 판서로 표시해 볼 수 있어요. "다음"을 누르면 웹캠이 닫힌 뒤 이어가요.',
      targetSelector:
        '[data-tutorial-slide="webcam-pause"], [aria-label="미리보기 일시정지"], [aria-label="미리보기 재생"], .cp-program-slide-webcam-canvas-sandwich, .cp-program-slide-webcam-draw-toolbar, .cp-program-slide-webcam-draw-toolbar-host',
      placement: 'below-nav',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      clickOnManualAdvanceSelectors:
        '[data-tutorial-slide="webcam-stop"], [aria-label="미리보기 정지"]',
      targetMissingHint:
        '웹캠 미리보기가 켜져 있어야 해요. 앞에서 웹캠 버튼으로 켠 뒤 다시 진행해 주세요.',
    },
    {
      id: 'nav-timer',
      mascot: '타이머 버튼을 눌러 수업용 타이머를 열어 주세요.',
      targetSelector: '[data-tutorial-slide="timer"]',
      followUp: {
        targetSelector:
          '[data-tutorial-slide="timer-modal"], .cp-program-slide-timer-modal',
        mascot:
          '타이머 모달이에요. 시간을 누적한 뒤 「시작」을 눌러 실행해 주세요. 「시작」을 눌러야 다음으로 진행돼요.',
        placement: 'left',
        /** 「시작」클릭으로만 진행 — "다음"으로 건너뛰기 불가 */
        advanceSelector:
          '[data-tutorial-slide="timer-start"]:not([disabled]), .cp-program-slide-timer-modal-start:not([disabled])',
      },
    },
    {
      id: 'nav-timer-running',
      mascot:
        '타이머가 실행되면 위쪽에 남은 시간이 크게 표시돼요. 시각이 표시된 영역을 클릭하면 일시정지했다가 다시 실행할 수 있어요. 써 본 뒤 "다음"을 누르면 타이머가 종료되고 이어가요.',
      targetSelector:
        '[data-tutorial-slide="timer-running"], [data-tutorial-slide="timer-running-bar"], .cp-program-slide-hero-timer-time, .cp-program-slide-timer-countdown--in-hero',
      /** 하이라이트(시각·타이머 막대) 바로 아래 — 막대를 가리지 않음 */
      placement: 'bottom',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      spotlightUnion: true,
      /** "다음" 시 실행 중인 타이머 중지 */
      clickOnManualAdvanceSelectors:
        '[data-tutorial-slide="timer"].is-on, [aria-label="타이머 중지"]',
      targetMissingHint:
        '타이머가 실행 중이어야 해요. 앞에서 「시작」을 누른 뒤 다시 진행해 주세요.',
    },
    {
      id: 'nav-bgm',
      mascot: 'BGM 버튼을 눌러 배경음 선택 창을 열어 주세요.',
      targetSelector: '[data-tutorial-slide="bgm"]',
      followUp: {
        targetSelector:
          '[data-tutorial-pres="bgm-modal"] .cp-program-slide-bgm-modal, .cp-program-slide-bgm-modal',
        mascot:
          'BGM 모달이에요. 분위기·트랙(또는 랜덤 플레이) 중 하나를 눌러 재생해 주세요. 트랙을 고르면 다음으로 진행돼요.',
        placement: 'left',
        /** 트랙/랜덤 클릭 시 BGM 종료 안내로 바로 진행 */
        advanceSelector: '[data-tutorial-slide="bgm-pick"]',
      },
    },
    {
      id: 'nav-bgm-stop',
      mascot:
        '재생 중에는 정지 버튼으로 BGM을 끌 수 있어요. 정지 버튼을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="bgm-stop"], [aria-label="BGM 정지"]',
      advanceSelector: '[data-tutorial-slide="bgm-stop"], [aria-label="BGM 정지"]',
      advanceImmediately: true,
      targetMissingHint:
        'BGM이 재생 중이어야 해요. 앞에서 트랙을 고른 뒤 다시 진행해 주세요.',
    },
    {
      id: 'nav-dice',
      mascot: '뽑기(주사위) 버튼을 눌러 뽑기 모달을 열어 주세요.',
      targetSelector: '[data-tutorial-slide="dice"]',
      advanceWhenSelectorExists:
        '[data-tutorial-pres="dice-modal"] .cp-slide-dice-modal, .cp-program-slide-dice-modal, .cp-slide-dice-modal',
    },
    {
      id: 'nav-dice-modal',
      mascot:
        '뽑기 모달이에요. 학생·자리·팀을 뽑아 볼 수 있어요. 살펴본 뒤 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-pres="dice-modal"] .cp-slide-dice-modal, .cp-program-slide-dice-modal, .cp-slide-dice-modal',
      placement: 'left',
      advanceWhenSelectorGone:
        '[data-tutorial-pres="dice-modal"] .cp-slide-dice-modal, .cp-program-slide-dice-modal, .cp-slide-dice-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      clickOnManualAdvanceSelectors:
        '[data-tutorial-pres="dice-modal"] [aria-label="닫기"], .cp-program-slide-dice-modal [aria-label="닫기"], .cp-slide-dice-modal [aria-label="닫기"]',
    },
    {
      id: 'nav-caption',
      mascot:
        '실시간 자막 버튼을 눌러 자막을 켜 주세요. 브라우저·마이크 권한이 필요할 수 있어요.',
      targetSelector: '[data-tutorial-slide="caption"]:not([disabled])',
      advanceWhenSelectorExists: '[data-tutorial-slide="caption-stop"], [aria-label="자막 끄기"]',
      manualAdvanceIfExists: '[data-tutorial-slide="caption-stop"], [aria-label="자막 끄기"]',
      targetMissingHint:
        '이 환경에서는 실시간 자막 버튼을 쓸 수 없을 수 있어요. "화면 다시 불러오기"로 복구하거나 튜토리얼을 이어 가세요.',
    },
    {
      id: 'nav-caption-stop',
      mascot:
        '자막을 끌 때는 정지 버튼을 눌러 주세요. 정지 버튼을 누르거나 "다음"을 누르면 자막이 꺼진 뒤 이어가요.',
      targetSelector: '[data-tutorial-slide="caption-stop"], [aria-label="자막 끄기"]',
      advanceSelector: '[data-tutorial-slide="caption-stop"], [aria-label="자막 끄기"]',
      advanceImmediately: true,
      manualAdvance: true,
      allowManualAdvanceActions: true,
      /** "다음" 시 정지 버튼 실행 후 진행 */
      clickOnManualAdvanceSelectors:
        '[data-tutorial-slide="caption-stop"], [aria-label="자막 끄기"]',
      targetMissingHint:
        '자막이 켜져 있어야 해요. 앞에서 자막을 켠 뒤 다시 진행하거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'nav-offline-html',
      mascot:
        '오른쪽 위 다운로드 버튼은 수업 슬라이드를 오프라인 HTML 파일로 저장해요. 내려받은 파일을 더블클릭하면 인터넷 없이도 슬라이드를 넘길 수 있어요. "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-slide="offline-html"], .cp-program-slide-offline-html-btn',
      manualAdvance: true,
      blockTargetClick: true,
      placement: 'bottom',
    },
    {
      id: 'open-question',
      mascot: '하단 「질문」탭을 눌러 질문 페이지로 이동해 주세요.',
      targetSelector:
        '[data-tutorial-slide="tab-question"], [data-footer-slide-tab="communication"], .cp-program-slide-dot.is-communication',
      advanceSelector:
        '[data-tutorial-slide="tab-question"], [data-footer-slide-tab="communication"], .cp-program-slide-dot.is-communication',
      advanceImmediately: true,
      /** 질문 화면·활성 탭이 보이면 클릭 누락이어도 진행 */
      advanceWhenSelectorExists:
        '[data-tutorial-communication="1"], [data-tutorial-slide="question-panel-host"], [data-tutorial-slide="question-panel"], .cp-program-slide-communication, .cp-program-slide-dot.is-communication.is-active, [data-footer-slide-tab="communication"][aria-selected="true"]',
      /** 이미 질문 페이지라면 "다음"으로도 진행 */
      manualAdvanceIfExists:
        '[data-tutorial-communication="1"], [data-tutorial-slide="question-panel-host"], [data-tutorial-slide="question-panel"], .cp-program-slide-communication, .cp-program-slide-dot.is-communication.is-active',
      allowManualAdvanceActions: true,
      /** 하단 탭을 말풍선이 가리지 않도록 위쪽에 배치 */
      placement: 'top',
    },
    {
      id: 'question-overview',
      mascot:
        '질문 페이지예요. 학생 응답을 모으고 결과 보기·정렬·표시 옵션을 조절할 수 있어요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="question-panel"], .cp-program-slide-communication',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[프레젠] 질문 페이지',
      sectionMascot: '질문·응답 보드 버튼을 알아보는 구간이에요.',
    },
    {
      id: 'question-group',
      mascot:
        '「멤버 그룹」에서 응답을 받을 그룹을 고를 수 있어요. 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="question-group"], [aria-label="멤버 그룹"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'question-view-mode',
      mascot:
        '결과 보기에서 「멤버별 응답」「정답·오답」「항목별」을 바꿔 볼 수 있어요. 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="question-view-mode"], [aria-label="결과 보기"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'question-sort',
      mascot:
        '정렬 버튼으로 번호순·응답순·무작위·자리배치 등을 바꿀 수 있어요. 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="question-sort"], [aria-label="정렬"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'question-options',
      mascot:
        '「응답」「이름」「정답」표시 토글이에요. 수업 프레젠테이션에서는 비공개로 하는 응답, 이름, 정답을 표시하여 확인할 수 있어요. 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="question-options"], [aria-label="표시 옵션"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'question-font',
      mascot:
        '학생 카드 글자 크기를 + / − 로 조절해요. 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="question-font"], [aria-label="학생 카드 폰트 크기"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'question-monitor',
      mascot:
        '「모니터링」은 원래 탭에서 질문 보드를 함께 볼 때 써요. 눌러 보거나 "다음"으로 넘어가도 돼요.',
      targetSelector: '[data-tutorial-slide="question-monitor"], [aria-label="모니터링"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'question-prompt',
      mascot: '가운데 질문/안내 문구를 눌러 문제 상세를 열어 주세요.',
      targetSelector: '[data-tutorial-slide="question-prompt"]',
      advanceWhenSelectorExists:
        '[data-tutorial-slide="question-detail-modal"], .cp-comm-q-modal',
    },
    {
      id: 'question-type-choice',
      mascot:
        '문제 상세 모달이에요. 「선택형」은 보기 중에서 고르는 문제예요. 「선택형」만 눌러 주세요.',
      targetSelector:
        '[data-tutorial-slide="question-type-choice"], text:선택형@.cp-comm-q-type-btn',
      advanceSelector:
        '[data-tutorial-slide="question-type-choice"], text:선택형@.cp-comm-q-type-btn',
      advanceImmediately: true,
      blockClickSelectors:
        '[data-tutorial-slide="question-type-short"], [data-tutorial-slide="question-type-essay"], [data-tutorial-slide="question-detail-close"], .cp-comm-q-modal-close, [data-tutorial-slide="question-detail-cancel"], .cp-comm-q-btn-cancel, .cp-comm-q-btn-save',
      targetMissingHint: '문제 상세 모달이 열려 있어야 해요. 질문 문구를 다시 눌러 주세요.',
    },
    {
      id: 'question-choice-correct',
      mascot:
        '선택지 왼쪽 번호를 누르면 정답으로 표시돼요. 번호를 한 번 눌러 정답을 설정해 주세요.',
      targetSelector: '[data-tutorial-slide="question-choice-correct"], .cp-comm-q-choice-num',
      advanceSelector: '[data-tutorial-slide="question-choice-correct"], .cp-comm-q-choice-num',
      advanceImmediately: true,
      advanceWhenSelectorExists: '.cp-comm-q-choice-num.is-correct',
      blockClickSelectors:
        '[data-tutorial-slide="question-type-choice"], [data-tutorial-slide="question-type-short"], [data-tutorial-slide="question-type-essay"], [data-tutorial-slide="question-detail-close"], .cp-comm-q-modal-close, [data-tutorial-slide="question-detail-cancel"], .cp-comm-q-btn-cancel, .cp-comm-q-btn-save',
      targetMissingHint:
        '선택형 화면이 열려 있어야 해요. 「선택형」을 누른 뒤 다시 진행해 주세요.',
    },
    {
      id: 'question-type-short',
      mascot: '이번에는 「단답」만 눌러 주세요. 짧은 답을 받는 문제 유형이에요.',
      targetSelector:
        '[data-tutorial-slide="question-type-short"], text:단답@.cp-comm-q-type-btn',
      advanceSelector:
        '[data-tutorial-slide="question-type-short"], text:단답@.cp-comm-q-type-btn',
      advanceImmediately: true,
      blockClickSelectors:
        '[data-tutorial-slide="question-type-choice"], [data-tutorial-slide="question-type-essay"], [data-tutorial-slide="question-detail-close"], .cp-comm-q-modal-close, [data-tutorial-slide="question-detail-cancel"], .cp-comm-q-btn-cancel, .cp-comm-q-btn-save',
      targetMissingHint: '문제 상세 모달이 열려 있어야 해요. 질문 문구를 다시 눌러 주세요.',
    },
    {
      id: 'question-short-multi',
      mascot:
        '단답은 「+정답 추가」로 복수 정답을 둘 수 있어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-slide="question-short-add-answer"], .cp-comm-q-add-dashed.is-label',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '단답 화면이 열려 있어야 해요. 「단답」을 누른 뒤 다시 진행해 주세요.',
    },
    {
      id: 'question-type-essay',
      mascot: '마지막으로 「서술형」만 눌러 주세요. 긴 답을 받고 정답 채점은 하지 않는 유형이에요.',
      targetSelector:
        '[data-tutorial-slide="question-type-essay"], text:서술형@.cp-comm-q-type-btn',
      advanceSelector:
        '[data-tutorial-slide="question-type-essay"], text:서술형@.cp-comm-q-type-btn',
      advanceImmediately: true,
      blockClickSelectors:
        '[data-tutorial-slide="question-type-choice"], [data-tutorial-slide="question-type-short"], [data-tutorial-slide="question-detail-close"], .cp-comm-q-modal-close, [data-tutorial-slide="question-detail-cancel"], .cp-comm-q-btn-cancel, .cp-comm-q-btn-save',
      targetMissingHint: '문제 상세 모달이 열려 있어야 해요. 질문 문구를 다시 눌러 주세요.',
    },
    {
      id: 'question-essay-guide',
      mascot:
        '서술형은 정답 판별이 없어요. 안내 문구만 두고 학생 답을 받습니다. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-slide="question-essay-guide"], .cp-comm-q-guide-input, [data-tutorial-slide="question-detail-modal"], .cp-comm-q-modal',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '서술형 화면이 열려 있어야 해요. 「서술형」을 누른 뒤 다시 진행해 주세요.',
    },
    {
      id: 'question-detail-close',
      mascot:
        '모달의 닫기(X)·취소하기를 누르거나 "다음"을 눌러 문제 상세를 닫아 주세요.',
      targetSelector: '[data-tutorial-slide="question-detail-modal"], .cp-comm-q-modal',
      advanceWhenSelectorGone:
        '[data-tutorial-slide="question-detail-modal"], .cp-comm-q-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      clickOnManualAdvanceSelectors:
        '[data-tutorial-slide="question-detail-close"], .cp-comm-q-modal-close',
      targetMissingHint: '문제 상세 모달이 열려 있어야 해요. 질문 문구를 다시 눌러 주세요.',
    },
    {
      id: 'question-nav',
      mascot:
        '이전·다음 질문으로 이동하거나, 질문을 삭제할 수 있어요. 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-slide="question-nav"], [aria-label="질문 탐색"]',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'outro',
      mascot:
        '수업 프레젠테이션 튜토리얼을 마쳤어요! 연간진도·프로그램 표의 「프레젠테이션 열기」로 언제든 다시 열 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const classPresentation: TutorialDefinition = {
  ...classPresentationRaw,
  steps: classPresentationRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint: buildTutorialTargetMissingHint(recover, step.targetMissingHint),
    };
  }),
};

export default classPresentation;
